/* ============
 * Vee Validate
 * ============
 *
 * Form validation plugin for Vue.js
 *
 * http://vee-validate.logaretm.com/
 */

import Vue from 'vue';
import { i18n } from '@/plugins/vue-i18n';
import VeeValidate, { Validator } from 'vee-validate';
import es from 'vee-validate/dist/locale/es';
import ca from 'vee-validate/dist/locale/ca';
import en from 'vee-validate/dist/locale/en';
import it from 'vee-validate/dist/locale/it';
import localeCA from '@/locale/ca/';
import localeEN from '@/locale/en/';
import localeES from '@/locale/es/';
import localeIT from '@/locale/it/';
import urlApi from '@/modules/url/api';

import {
  isNil, isNumber, isObject, isUndefined,
} from 'lodash';
import moment from 'moment';
import vvConfig from './vee-validate-config';

// Localize takes the locale object as the second argument (optional) and merges it.
const locateWatcher = {
  es,
  ca,
  en,
  it,
}[i18n.locale];

Validator.localize(i18n.locale, locateWatcher);

/**
 * Custom validators  for Vee-validate
 */

/**
 * Relation validator
 */
Validator.extend('relation', {
  getMessage(field) {
    const relation = i18n.te(`message.content.relation.${field}`)
      ? i18n.t(`message.content.relation.${field}`) : field;
    return i18n.t('message.content.relation.error.invalidRelation', { relation });
  },
  validate(value, args) {
    if (args[0] === 'valid') {
      return true;
    }
    // Every relation item value includes a relationValidation property
    const { relationValidation } = value ? value[0] || {} : {};
    if (relationValidation) {
      return relationValidation.valid;
    }
    return false;
  },
});

Validator.extend('unique_url', {
  getMessage() {
    return i18n.t('message.url.error.notUnique');
  },
  async validate(value) {
    try {
      return await urlApi.isUnique(value);
    } catch (e) {
      return false;
    }
  },
});

Validator.extend('nx_valid_url', {
  getMessage(field) {
    return i18n.t('message.url.error.notValidForField', { field });
  },
  validate(value) {
    if (!value) return true;
    if (!value.path) return false;
    if (value.type === 'boardUrl'
      && !value.autogenerateBoard
      && (!value.entity || !value.entity.id)) {
      return false;
    }
    if (value.type === 'redirectUrl') {
      if (!value.redirectCode) {
        return false;
      }
      if (Math.floor(value.redirectCode / 100) === 3
        && (!value.destination)) {
        return false;
      }
    }
    return true;
  },
});

Validator.extend('contains_uppercase', {
  getMessage() {
    return i18n.t('message.password.error.passwordUpperCaseError');
  },
  validate(value) {
    return /[A-Z]/.test(value);
  },
});

Validator.extend('contains_lowercase', {
  getMessage() {
    return i18n.t('message.password.error.passwordLowerCaseError');
  },
  validate(value) {
    return /[a-z]/.test(value);
  },
});

Validator.extend('contains_number', {
  getMessage() {
    return i18n.t('message.password.error.passwordNumberError');
  },
  validate(value) {
    return /[0-9]/.test(value);
  },
});

Validator.extend('contains_special_character', {
  getMessage() {
    return i18n.t('message.password.error.passwordSpecialCharacterError');
  },
  validate(value) {
    return /[#?!.@$%^&*-]/.test(value);
  },
});

Validator.extend('max_same_consecutive_chars', {
  getMessage(field, arg) {
    const maxNumber = arg[0];
    return i18n.t('message.password.error.passwordMaxConsecutiveCharactersError', { number: maxNumber });
  },
  validate(value, arg) {
    const maxNumber = arg[0] - 1;
    const regexp = new RegExp(`([a-zA-Z0-9#?!.@$%^&*-])\\1{${maxNumber},}`);
    return !value.match(regexp);
  },
});

Validator.extend('new_password', {
  getMessage() {
    return i18n.t('message.password.error.passwordMatchesOldOneError');
  },
  validate(value, [oldPassword]) {
    return value !== oldPassword;
  },
});


const validateLinkedSelectValue = (key, selectValue) => {
  if (!key) return true;
  return !isNil(selectValue[key]);
};
Validator.extend('nx_valid_linked_selects', {
  getMessage(field) {
    return i18n.t('message.error.required', { field });
  },
  validate(value, args) {
    const [master, slave] = (Array.isArray(args) ? args : []);
    if (!isObject(value)) {
      return false;
    }
    return validateLinkedSelectValue(master, value)
      && validateLinkedSelectValue(slave, value);
  },
}, {
  paramNames: ['master', 'slave'],
});


Validator.extend('nx_entity_url', (value, arg) => {
  if (!value) {
    return false;
  }
  if (vvConfig.nxEntityUrlEmptyTypes.includes(value.type)) {
    return true;
  }
  if (value.type === 'manual') {
    if (!value.url) return false;
    // eslint-disable-next-line no-useless-escape
    const isValidUrl = new RegExp('^((?:https?:\/\/|\/\/)[a-zA-Z0-9-.]+(:\\d+)?)?((\/.*)?\/(?!\/).*)?$', 'i');
    if (!Array.isArray(arg) || !arg.length || arg[0] !== 'true') {
      return isValidUrl.test(value.url);
    }
    // eslint-disable-next-line no-useless-escape
    const isValidJavaScriptCall = new RegExp('(javascript:).*\(\)');
    return isValidUrl.test(value.url) || isValidJavaScriptCall.test(value.url);
  }

  return !!value.entity;
});

Validator.extend('quillCharacterLength', {
  getMessage() {
    return i18n.t('message.content.editor.errors.bodyCharacterLimitError');
  },
  validate(value, args) {
    return args[0] === 'valid';
  },
});

Validator.extend('quillWordLength', {
  getMessage() {
    return i18n.t('message.content.editor.errors.bodyWordLimitError');
  },
  validate(value, args) {
    return args[0] === 'valid';
  },
});

Validator.extend('validateWithMinDate', (value, minDate) => moment(minDate)
  .isBefore(value));

Validator.extend('validateWithMaxDate', (value, maxDate) => moment(maxDate)
  .isAfter(value));

Validator.extend('empty_if', {
  getMessage(field) {
    return i18n.t(`message.error.empty.${field.toLowerCase()}`);
  },
  validate(value, [otherFieldValue]) {
    // If other field is empty, validation is OK
    return !!otherFieldValue === false;
  },
});

Validator.extend('is_this_object_id', {
  getMessage() {
    return i18n.t('message.error.invalidSelectedObject');
  },
  validate(value, id) {
    if (isObject(value)) {
      if (!isUndefined(value.id)
        && value.id !== id.shift()) {
        return false;
      }
    }
    return !(isNumber(value)
      && value !== id.shift());
  },
});

Validator.extend('is_not_this_object_id', {
  getMessage() {
    return i18n.t('message.error.invalidSelectedObject');
  },
  validate(value, id) {
    if (isObject(value)) {
      if (!isUndefined(value.id)
        && value.id === id.shift()) {
        return false;
      }
    }
    if (isNumber(value)
      && value === id.shift()) {
      return false;
    }
    return true;
  },
});

Validator.extend('second_factor_otp', {
  getMessage() {
    return i18n.t('message.error.invalidSecondFactorOtp');
  },
  async validate(value) {
    return /\d{6}/.test(value);
  },
});

const config = {
  errorBagName: 'errors', // change if property conflicts
  fieldsBagName: 'fields',
  delay: 200,
  // dictionary: null,
  dictionary: {
    ca: {
      messages: localeCA.validation,
      attributes: localeCA.form.fields,
    },
    en: {
      messages: localeEN.validation,
      attributes: localeEN.form.fields,
    },
    es: {
      messages: localeES.validation,
      attributes: localeES.form.fields,
    },
    it: {
      messages: localeIT.validation,
      attributes: localeIT.form.fields,
    },
  },
  strict: true,
  classes: false,
  classNames: {
    touched: 'touched', // the control has been blurred
    untouched: 'untouched', // the control hasn't been blurred
    valid: 'valid', // model is valid
    invalid: 'invalid', // model is invalid
    pristine: 'pristine', // control has not been interacted with
    dirty: 'dirty', // control has been interacted with
  },
  events: 'blur',
  inject: false,
  validity: false,
  aria: true,
};

Vue.use(VeeValidate, config);

export default Validator;
